import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { DISPOSITIONS, DISPOSITION_HINTS, DISPOSITION_LABELS, apiError, formatMinor, toMinor, todayIso } from "../../lib/finance";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Totals of the selected advances, per currency, never converted.
function totalsOf(items) {
  const totals = {};
  items.forEach((i) => { totals[i.currency] = (totals[i.currency] || 0) + (toMinor(i.amount, i.currency) || 0); });
  return Object.entries(totals).sort(([a], [b]) => a.localeCompare(b)).map(([c, v]) => formatMinor(v, c));
}

// Records a decision taken collectively outside the CRM (meeting, minutes) on the selected advances. A new
// decision replaces the previous one on each payment; both stay in the expense history.
export default function DecisionDialog({ items, payerLabel, open, onOpenChange, onRecorded }) {
  const [status, setStatus] = useState("apport");
  const [decidedOn, setDecidedOn] = useState(todayIso());
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const key = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStatus("apport"); setDecidedOn(todayIso()); setReference(""); setNote(""); setShowErrors(false);
    key.current = crypto.randomUUID(); // one key per opening: a double click cannot record the decision twice
  }, [open]);

  const dateError = !decidedOn ? "Indiquez la date de la décision." : decidedOn > todayIso() ? "La date ne peut pas être dans le futur." : "";

  const save = async () => {
    setShowErrors(true);
    if (dateError) return;
    setSaving(true);
    try {
      await api.post("/finance/decisions", {
        status, decided_on: decidedOn, reference: reference.trim() || null, note: note.trim() || null,
        items: items.map((i) => ({ expense_id: i.expense_id, funding_id: i.funding_id })),
      }, { headers: { "Idempotency-Key": key.current } });
      toast.success(`Décision consignée sur ${items.length} avance${items.length > 1 ? "s" : ""}`);
      onRecorded();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="finance-decision-dialog">
        <DialogHeader>
          <DialogTitle>Consigner une décision</DialogTitle>
          <DialogDescription>
            {items.length} avance{items.length > 1 ? "s" : ""} de {payerLabel} : <strong className="tabular-nums">{totalsOf(items).join(" · ")}</strong>.
            La décision a été prise entre associés ; le CRM la garde en mémoire. Elle remplace la décision précédente de chaque avance, qui reste dans l'historique.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-900 mb-1">Décision</legend>
            {DISPOSITIONS.map((d) => (
              <label key={d} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer ${status === d ? "border-jamora bg-jamora-50/50" : "border-slate-200"}`}>
                <input type="radio" name="decision" value={d} checked={status === d} onChange={() => setStatus(d)} className="mt-1 accent-jamora"
                  data-testid={`finance-decision-${d}`} />
                <span>
                  <span className="block text-sm text-slate-900">{d === "a_decider" ? "Remettre à décider" : DISPOSITION_LABELS[d]}</span>
                  <span className="block text-xs text-slate-500">{DISPOSITION_HINTS[d]}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="fin-decision-date">Date de la décision</Label>
              <Input id="fin-decision-date" type="date" max={todayIso()} value={decidedOn} onChange={(e) => setDecidedOn(e.target.value)}
                className="text-base md:text-sm" aria-invalid={showErrors && !!dateError} data-testid="finance-decision-date" />
              {showErrors && dateError && <p className="text-xs text-red-700">{dateError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="fin-decision-ref">Référence</Label>
              <Input id="fin-decision-ref" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120}
                placeholder="Ex. : PV AG du 15/10/2026" className="text-base md:text-sm" data-testid="finance-decision-reference" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fin-decision-note">Note</Label>
            <Textarea id="fin-decision-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={2}
              placeholder="Facultatif : précisions utiles plus tard." className="text-base md:text-sm" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="finance-decision-save">
            {saving && <Loader2 size={14} className="animate-spin mr-1.5" />} Consigner la décision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
